import type { I18n } from '@lingui/core';
import { t } from '@lingui/core/macro';

/**
 * Converts an ISO date string to a human-readable relative time string.
 * Uses Intl.RelativeTimeFormat for proper localization.
 */
export const relativeTime = (i18n: I18n, isoString: string): string => {
  const now = new Date();
  const then = new Date(isoString);
  const diffMs = now.getTime() - then.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  if (diffSec < 60) {
    return t`just now`;
  }

  const rtf = new Intl.RelativeTimeFormat(i18n.locale, { numeric: 'auto' });

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) {
    return rtf.format(-diffMin, 'minute');
  }

  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) {
    return rtf.format(-diffH, 'hour');
  }

  const diffD = Math.floor(diffH / 24);
  if (diffD >= 7) {
    return absoluteTimeHuman(i18n, isoString);
  }
  return rtf.format(-diffD, 'day');
};

/**
 * Formats an ISO date string to a localized absolute date-time string.
 */
export const absoluteTime = (i18n: I18n, isoString: string): string => {
  return i18n.date(new Date(isoString), {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats an ISO date string to a human-readable absolute date-time string.
 * Month as text, day without leading zero, omits year if current year.
 */
export const absoluteTimeHuman = (i18n: I18n, isoString: string): string => {
  const date = new Date(isoString);
  const now = new Date();
  const currentYear = now.getFullYear();

  return i18n.date(date, {
    year: date.getFullYear() === currentYear ? undefined : 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Formats a duration in seconds to MM:SS string.
 */
export const formatDuration = (seconds: number): string => {
  // const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);

  return [String(m).padStart(2, '0'), String(s).padStart(2, '0')].join(':');
};
